import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Bot, TrendingUp, Shield, Megaphone, Users, Workflow,
  Play, Clock, CheckCircle2, AlertCircle, ChevronRight,
  Zap, ArrowLeft, Loader2, Sparkles, BarChart3, XCircle, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { TrendingUp, Shield, Megaphone, Users, Workflow, Bot, Zap, BarChart3, Sparkles };

const CATEGORY_COLORS = {
  growth: { bg: 'var(--accent-blue-200)', color: '#06B6D4', border: 'rgba(6,182,212,0.2)' },
  finance: { bg: 'var(--accent-green-200)', color: '#059669', border: 'rgba(5,150,105,0.2)' },
  marketing: { bg: 'var(--accent-orange-200)', color: '#F59E0B', border: 'rgba(245,158,11,0.2)' },
  hr: { bg: 'var(--accent-purple-200)', color: '#6366F1', border: 'rgba(99,102,241,0.2)' },
  operations: { bg: 'var(--accent-pink-200)', color: '#E11D48', border: 'rgba(225,29,72,0.2)' },
  custom: { bg: 'var(--accent-grey-200)', color: '#64748B', border: 'rgba(100,116,139,0.2)' },
};

const CONTEXT_HINTS = {
  nl: {
    'sales-pipeline': 'Bijv: "B2B SaaS, doelgroep: mkb accountants, focus op Nederland en Belgie"',
    'financial-health': 'Bijv: "Jaaromzet 500K, 8 medewerkers, seizoensgebonden omzet in Q4"',
    'content-strategy': 'Bijv: "LinkedIn en nieuwsbrief, doelgroep: HR managers, tone of voice: informeel"',
    'hiring-pipeline': 'Bijv: "Senior Developer, remote, React + Python, budget 60-80K"',
    'process-optimizer': 'Bijv: "Focus op de facturatie- en onboarding processen, 3 man team"',
    _default: 'Bijv: "20 medewerkers, B2B sector, focus op groei in Benelux"',
  },
  en: {
    'sales-pipeline': 'E.g.: "B2B SaaS, target audience: SMB accountants, focus on Netherlands and Belgium"',
    'financial-health': 'E.g.: "Annual revenue 500K, 8 employees, seasonal revenue in Q4"',
    'content-strategy': 'E.g.: "LinkedIn and newsletter, target audience: HR managers, tone: informal"',
    'hiring-pipeline': 'E.g.: "Senior Developer, remote, React + Python, budget 60-80K"',
    'process-optimizer': 'E.g.: "Focus on invoicing and onboarding processes, 3-person team"',
    _default: 'E.g.: "20 employees, B2B sector, focus on Benelux growth"',
  },
};

const labels = {
  nl: {
    title: 'AI Agents',
    subtitle: 'Autonome AI workflows die meerdere tools combineren tot krachtige pipelines',
    templates: 'Agent Templates',
    myAgents: 'Mijn Agents',
    activity: 'Activiteit',
    activate: 'Activeren',
    activated: 'Geactiveerd',
    run: 'Uitvoeren',
    running: 'Bezig...',
    steps: 'stappen',
    noAgents: 'Nog geen agents geactiveerd — activeer er één om te beginnen.',
    noAgentsDesc: 'Kies een template om je eerste AI agent te starten',
    noRuns: 'Nog geen agent-activiteit.',
    noRunsDesc: 'Voer een agent uit om hier de resultaten te zien',
    back: 'Terug',
    contextLabel: 'Bedrijfscontext',
    contextDesc: 'Geef specifieke informatie over je bedrijf zodat de agent relevantere en nauwkeurigere resultaten kan genereren.',
    synthesis: 'Executive Summary',
    stepResults: 'Stap Resultaten',
    completed: 'Voltooid',
    failed: 'Mislukt',
    lastRun: 'Laatste run',
    runs: 'runs',
    deleteAgent: 'Verwijderen',
    progressTitle: 'Agent is bezig...',
    progressWait: 'Dit duurt ongeveer 30-60 seconden. Je kunt deze pagina open laten.',
    stepPending: 'Wachtend',
    stepRunning: 'Bezig',
    stepDone: 'Voltooid',
  },
  en: {
    title: 'AI Agents',
    subtitle: 'Autonomous AI workflows that combine multiple tools into powerful pipelines',
    templates: 'Agent Templates',
    myAgents: 'My Agents',
    activity: 'Activity',
    activate: 'Activate',
    activated: 'Activated',
    run: 'Run',
    running: 'Running...',
    steps: 'steps',
    noAgents: 'No agents activated yet — activate one to get started.',
    noAgentsDesc: 'Choose a template to start your first AI agent',
    noRuns: 'No agent activity yet.',
    noRunsDesc: 'Run an agent to see results here',
    back: 'Back',
    contextLabel: 'Business context',
    contextDesc: 'Provide specific information about your business so the agent can generate more relevant and accurate results.',
    synthesis: 'Executive Summary',
    stepResults: 'Step Results',
    completed: 'Completed',
    failed: 'Failed',
    lastRun: 'Last run',
    runs: 'runs',
    deleteAgent: 'Delete',
    progressTitle: 'Agent is working...',
    progressWait: 'This takes approximately 30-60 seconds. You can keep this page open.',
    stepPending: 'Pending',
    stepRunning: 'Running',
    stepDone: 'Completed',
  },
};


export default function AgentWorkflows() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = labels[lang] || labels.nl;
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [myAgents, setMyAgents] = useState([]);
  const [recentRuns, setRecentRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runProgress, setRunProgress] = useState(null); // { run_id, step_labels, completed }
  const [extraContext, setExtraContext] = useState('');
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, agentRes, runsRes] = await Promise.all([
        fetch(`${API}/api/workflows/templates`, { headers }),
        fetch(`${API}/api/workflows`, { headers }),
        fetch(`${API}/api/workflows/runs/recent`, { headers }),
      ]);
      if (tplRes.ok) setTemplates((await tplRes.json()).templates || []);
      if (agentRes.ok) setMyAgents((await agentRes.json()).workflows || []);
      if (runsRes.ok) setRecentRuns((await runsRes.json()).runs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const activateTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API}/api/workflows`, {
        method: 'POST', headers,
        body: JSON.stringify({ template_id: templateId }),
      });
      if (res.ok) { setTab('myAgents'); fetchData(); }
    } catch (e) { console.error(e); }
  };

  const startPolling = (runId, stepLabels) => {
    setRunProgress({ run_id: runId, step_labels: stepLabels, completed: 0, status: 'running' });
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/workflows/runs/${runId}`, { headers });
        if (!res.ok) return;
        const data = await res.json();
        const completedCount = (data.step_results || []).length;
        setRunProgress(prev => ({ ...prev, completed: completedCount, status: data.status }));

        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunProgress(null);
          setSelectedRun(data);
          fetchData();
        }
      } catch (e) { console.error(e); }
    }, 3000);
  };

  const runWorkflow = async (workflowId) => {
    try {
      const res = await fetch(`${API}/api/workflows/${workflowId}/run`, {
        method: 'POST', headers,
        body: JSON.stringify({ additional_context: extraContext }),
      });
      if (res.ok) {
        const data = await res.json();
        const stepLabelsForLang = lang === 'en' ? (data.step_labels_en || data.step_labels) : data.step_labels;
        startPolling(data.run_id, stepLabelsForLang);
        setExtraContext('');
      }
    } catch (e) { console.error(e); }
  };

  const viewRun = async (runId) => {
    try {
      const res = await fetch(`${API}/api/workflows/runs/${runId}`, { headers });
      if (res.ok) setSelectedRun(await res.json());
    } catch (e) { console.error(e); }
  };

  const deleteAgent = async (workflowId) => {
    try {
      await fetch(`${API}/api/workflows/${workflowId}`, { method: 'DELETE', headers });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const getIcon = (name) => ICON_MAP[name] || Bot;

  // ─── Progress overlay ──────────────────────────────────────
  if (runProgress) {
    const { step_labels, completed, status } = runProgress;
    const total = step_labels.length;
    const synthLabel = lang === 'en' ? 'Synthesis' : 'Synthese';

    return (
      <div className="tool-page" data-testid="agent-progress">
        <div style={{ maxWidth: 640, margin: '3rem auto', textAlign: 'center' }}>
          <div style={{
            width: '4rem', height: '4rem', borderRadius: '50%', margin: '0 auto 1.5rem',
            background: 'var(--accent-purple-200)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Loader2 size={24} style={{ color: '#6366F1', animation: 'spin 1.5s linear infinite' }} />
          </div>
          <h2 className="heading-2" data-testid="progress-title">{t.progressTitle}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem', marginBottom: '2.5rem' }}>
            {t.progressWait}
          </p>

          {/* Stepper */}
          <div data-testid="progress-stepper" style={{ display: 'flex', flexDirection: 'column', gap: 0, textAlign: 'left' }}>
            {(Array.isArray(step_labels) ? step_labels : []).map((label, i) => {
              const isDone = i < completed;
              const isCurrent = i === completed && status === 'running';
              const isPending = i > completed;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  {/* Dot + connector line */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '2rem', flexShrink: 0 }}>
                    <div style={{
                      width: '1.5rem', height: '1.5rem', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isDone ? '#059669' : isCurrent ? '#6366F1' : 'var(--bg-section)',
                      border: isPending ? '2px solid var(--border-primary)' : 'none',
                      transition: 'background 0.4s, border 0.3s',
                    }}>
                      {isDone && <CheckCircle2 size={14} style={{ color: 'white' }} />}
                      {isCurrent && <Loader2 size={12} style={{ color: 'white', animation: 'spin 1s linear infinite' }} />}
                    </div>
                    {i < total - 1 && (
                      <div style={{
                        width: '2px', height: '2rem',
                        background: isDone ? '#059669' : 'var(--border-primary)',
                        transition: 'background 0.4s',
                      }} />
                    )}
                    {i === total - 1 && (
                      <div style={{
                        width: '2px', height: '2rem',
                        background: completed >= total ? '#059669' : 'var(--border-primary)',
                        transition: 'background 0.4s',
                      }} />
                    )}
                  </div>
                  {/* Label */}
                  <div style={{
                    paddingTop: '0.1rem', paddingBottom: '1.1rem',
                    fontWeight: isCurrent ? 600 : 450,
                    color: isDone ? '#059669' : isCurrent ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontSize: '0.88rem',
                    transition: 'color 0.3s',
                  }}>
                    {label}
                    {isDone && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.7 }}>{t.stepDone}</span>}
                    {isCurrent && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6366F1' }}>{t.stepRunning}</span>}
                  </div>
                </div>
              );
            })}
            {/* Synthesis step */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '2rem', flexShrink: 0 }}>
                <div style={{
                  width: '1.5rem', height: '1.5rem', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: completed > total ? '#059669' : completed === total ? '#6366F1' : 'var(--bg-section)',
                  border: completed < total ? '2px solid var(--border-primary)' : 'none',
                }}>
                  {completed === total && status === 'running' && <Loader2 size={12} style={{ color: 'white', animation: 'spin 1s linear infinite' }} />}
                  {status === 'completed' && <Sparkles size={12} style={{ color: 'white' }} />}
                </div>
              </div>
              <div style={{
                paddingTop: '0.1rem',
                fontWeight: completed >= total ? 600 : 450,
                color: completed >= total ? (status === 'completed' ? '#059669' : 'var(--text-primary)') : 'var(--text-muted)',
                fontSize: '0.88rem',
              }}>
                {synthLabel}
                {completed === total && status === 'running' && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6366F1' }}>{t.stepRunning}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Run detail view ───────────────────────────────────────
  if (selectedRun) {
    return (
      <div className="tool-page" data-testid="agent-run-detail">
        <button onClick={() => setSelectedRun(null)} className="btn-secondary" style={{ marginBottom: '1.5rem', gap: '0.4rem' }} data-testid="run-detail-back">
          <ArrowLeft size={16} /> {t.back}
        </button>
        <div style={{ marginBottom: '2rem' }}>
          <h1 className="heading-1" data-testid="run-detail-title">{selectedRun.workflow_name || 'Agent Run'}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
            {selectedRun.started_at?.slice(0, 16).replace('T', ' ')} &middot;
            {selectedRun.status === 'completed' ?
              <span style={{ color: '#059669' }}> {t.completed}</span> :
              <span style={{ color: '#E11D48' }}> {t.failed}</span>}
          </p>
        </div>
        {selectedRun.synthesis && (
          <div data-testid="run-synthesis" style={{
            background: 'linear-gradient(135deg, var(--accent-purple-200), #E0E7FF)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem',
          }}>
            <h3 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Sparkles size={18} style={{ color: '#6366F1' }} /> {t.synthesis}
            </h3>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              {selectedRun.synthesis}
            </div>
          </div>
        )}
        <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{t.stepResults}</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {(selectedRun.step_results || []).map((step, i) => (
            <StepCard key={i} step={step} index={i} t={t} />
          ))}
        </div>
      </div>
    );
  }

  // ─── Main view ─────────────────────────────────────────────
  return (
    <div className="tool-page" data-testid="agent-workflows-page">
      <div style={{ marginBottom: '2rem' }}>
        <h1 className="heading-1" data-testid="workflows-title">{t.title}</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem' }}>{t.subtitle}</p>
      </div>

      {/* Tabs */}
      <div data-testid="workflow-tabs" style={{
        display: 'flex', gap: '0.25rem', marginBottom: '2rem',
        background: 'var(--bg-section)', borderRadius: '10px', padding: '4px', width: 'fit-content',
      }}>
        {['templates', 'myAgents', 'activity'].map((key) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)} style={{
            padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none',
            fontSize: '0.82rem', fontWeight: tab === key ? 600 : 450, cursor: 'pointer',
            background: tab === key ? 'var(--bg-card)' : 'transparent',
            color: tab === key ? 'var(--text-primary)' : 'var(--text-muted)',
            boxShadow: tab === key ? 'var(--shadow-card)' : 'none',
            transition: 'all 0.2s ease',
          }}>
            {t[key]}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-muted)' }} />
        </div>
      ) : (
        <>
          {/* Templates Tab */}
          {tab === 'templates' && (
            <div data-testid="templates-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.25rem' }}>
              {(Array.isArray(templates) ? templates : []).map((tpl) => {
                const Icon = getIcon(tpl.icon);
                const colors = CATEGORY_COLORS[tpl.category] || CATEGORY_COLORS.custom;
                const isActivated = (Array.isArray(myAgents) ? myAgents : []).some(a => a.template_id === tpl.template_id);
                return (
                  <div key={tpl.template_id} data-testid={`template-${tpl.template_id}`} style={{
                    background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem',
                    border: `1px solid ${colors.border}`, boxShadow: 'var(--shadow-card)',
                    transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'default',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-card)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div style={{
                        width: '3rem', height: '3rem', borderRadius: '10px',
                        background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon size={20} style={{ color: colors.color }} />
                      </div>
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                        letterSpacing: '0.05em', color: colors.color, background: colors.bg,
                        padding: '0.25rem 0.6rem', borderRadius: '6px',
                      }}>
                        {tpl.steps.length} {t.steps}
                      </span>
                    </div>
                    <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1.05rem', marginBottom: '0.5rem' }}>
                      {lang === 'en' ? tpl.name_en : tpl.name_nl}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                      {lang === 'en' ? tpl.description_en : tpl.description_nl}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                      {(Array.isArray(tpl.steps) ? tpl.steps : []).map((s, i) => (
                        <span key={i} style={{
                          fontSize: '0.72rem', background: 'var(--bg-section)', color: 'var(--text-secondary)',
                          padding: '0.2rem 0.5rem', borderRadius: '6px', fontWeight: 450,
                        }}>
                          {i + 1}. {lang === 'en' ? s.label_en : s.label_nl}
                        </span>
                      ))}
                    </div>
                    <button data-testid={`activate-${tpl.template_id}`}
                      onClick={() => !isActivated && activateTemplate(tpl.template_id)}
                      disabled={isActivated}
                      className={isActivated ? 'btn-secondary' : 'btn-primary'}
                      style={{ width: '100%', opacity: isActivated ? 0.6 : 1 }}
                    >
                      {isActivated ? <><CheckCircle2 size={15} /> {t.activated}</> : <><Zap size={15} /> {t.activate}</>}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* My Agents Tab */}
          {tab === 'myAgents' && (
            myAgents.length === 0 ? (
              <EmptyState icon={Bot} title={t.noAgents} desc={t.noAgentsDesc} action={() => setTab('templates')} actionLabel={t.templates} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {(Array.isArray(myAgents) ? myAgents : []).map((agent) => {
                  const Icon = getIcon(agent.icon);
                  const colors = CATEGORY_COLORS[agent.category] || CATEGORY_COLORS.custom;
                  const hint = (CONTEXT_HINTS[lang] || CONTEXT_HINTS.nl)[agent.template_id] || (CONTEXT_HINTS[lang] || CONTEXT_HINTS.nl)._default;
                  return (
                    <div key={agent.workflow_id} data-testid={`agent-${agent.workflow_id}`} style={{
                      background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem',
                      border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-card)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                        <div style={{
                          width: '2.75rem', height: '2.75rem', borderRadius: '10px',
                          background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Icon size={18} style={{ color: colors.color }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1rem' }}>{agent.name}</h3>
                          <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                            {agent.steps.length} {t.steps} &middot; {agent.run_count || 0} {t.runs}
                            {agent.last_run_at && <> &middot; {t.lastRun}: {agent.last_run_at.slice(0, 10)}</>}
                          </p>
                        </div>
                        <button data-testid={`delete-${agent.workflow_id}`}
                          onClick={() => deleteAgent(agent.workflow_id)}
                          className="btn-secondary" style={{ color: '#E11D48', padding: '0.5rem' }}
                        >
                          <XCircle size={16} />
                        </button>
                      </div>

                      {/* Context input with explanation */}
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem',
                          fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem',
                        }}>
                          <Info size={14} style={{ color: '#6366F1' }} /> {t.contextLabel}
                        </label>
                        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem', lineHeight: 1.5 }}>
                          {t.contextDesc}
                        </p>
                        <textarea
                          data-testid={`context-${agent.workflow_id}`}
                          placeholder={hint}
                          onChange={e => setExtraContext(e.target.value)}
                          rows={2}
                          style={{
                            width: '100%', background: 'var(--bg-section)', border: '1px solid var(--border-light)',
                            borderRadius: '8px', padding: '0.6rem 0.75rem', fontSize: '0.82rem',
                            resize: 'vertical', fontFamily: 'inherit', color: 'var(--text-primary)',
                          }}
                        />
                      </div>

                      <button data-testid={`run-${agent.workflow_id}`}
                        onClick={() => runWorkflow(agent.workflow_id)}
                        className="btn-primary" style={{ width: '100%' }}
                      >
                        <Play size={15} /> {t.run}
                      </button>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Activity Tab */}
          {tab === 'activity' && (
            recentRuns.length === 0 ? (
              <EmptyState icon={Clock} title={t.noRuns} desc={t.noRunsDesc} action={() => setTab('myAgents')} actionLabel={t.myAgents} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(Array.isArray(recentRuns) ? recentRuns : []).map((run) => (
                  <button key={run.run_id} data-testid={`run-item-${run.run_id}`}
                    onClick={() => viewRun(run.run_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '1rem',
                      background: 'var(--bg-card)', borderRadius: '12px', padding: '1rem 1.25rem',
                      border: '1px solid var(--border-light)', cursor: 'pointer',
                      transition: 'transform 0.15s, box-shadow 0.15s', textAlign: 'left', width: '100%',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = 'var(--shadow-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{
                      width: '2.5rem', height: '2.5rem', borderRadius: '10px',
                      background: run.status === 'completed' ? 'var(--accent-green-200)' : 'var(--accent-pink-200)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {run.status === 'completed' ? <CheckCircle2 size={18} style={{ color: '#059669' }} /> : <AlertCircle size={18} style={{ color: '#E11D48' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{run.workflow_name || 'Agent Run'}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {run.started_at?.slice(0, 16).replace('T', ' ')}
                      </div>
                    </div>
                    <ChevronRight size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}


function StepCard({ step, index, t }) {
  const [expanded, setExpanded] = useState(index === 0);
  return (
    <div data-testid={`step-card-${index}`} style={{
      background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border-light)',
      overflow: 'hidden', boxShadow: 'var(--shadow-card)',
    }}>
      <button onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem',
        width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}>
        <div style={{
          width: '1.75rem', height: '1.75rem', borderRadius: '50%',
          background: step.status === 'completed' ? '#059669' : '#E11D48',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
        }}>
          {step.status === 'completed' ? <CheckCircle2 size={14} /> : index + 1}
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{step.label}</span>
          <span style={{ marginLeft: '0.75rem', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
            {step.duration_seconds}s
          </span>
        </div>
        <ChevronRight size={16} style={{
          color: 'var(--text-muted)', transition: 'transform 0.2s',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
        }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--border-light)', paddingTop: '1rem' }}>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {step.result}
          </div>
        </div>
      )}
    </div>
  );
}


function EmptyState({ icon: Icon, title, desc, action, actionLabel }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem 2rem', textAlign: 'center' }}>
      <div style={{
        width: '4rem', height: '4rem', borderRadius: '50%',
        background: 'var(--accent-purple-200)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem',
      }}>
        <Icon size={24} style={{ color: '#6366F1' }} />
      </div>
      <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>{title}</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>{desc}</p>
      {action && <button onClick={action} className="btn-primary">{actionLabel}</button>}
    </div>
  );
}
